import { useState, useEffect } from "react";
import { adminApi } from "@/utils/adminApi";
import { KeyRound, Link2, Copy, Check, Save, Eye, EyeOff, RotateCcw } from "lucide-react";
import { PinKeypad, PinDots } from "@/components/admin/PinKeypad";

const PIN_LENGTH = 6;

export const AccessCodeSettings = () => {
  const [code, setCode] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [step, setStep] = useState(1); // 1 = enter, 2 = confirm
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    adminApi.get("/api/admin/settings/access-code")
      .then(r => setCode(r.data.access_code))
      .catch(() => {});
  }, []);

  const magicUrl = code ? `${window.location.origin}/admin?key=${code}` : "";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(magicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked - user can select the text manually
    }
  };

  const reset = () => {
    setNewPin("");
    setConfirmPin("");
    setStep(1);
    setMsg(null);
  };

  const onChangeFirst = (v) => {
    setNewPin(v);
    if (v.length === PIN_LENGTH) setStep(2);
  };

  const onChangeSecond = (v) => {
    setConfirmPin(v);
    if (v.length === PIN_LENGTH) {
      if (v !== newPin) {
        setMsg({ type: "err", text: "PINs don't match. Start over." });
        reset();
      } else {
        save(v);
      }
    }
  };

  const save = async (pin) => {
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await adminApi.put("/api/admin/settings/access-code", { new_code: pin });
      setCode(data.access_code);
      setMsg({ type: "ok", text: "PIN updated. Re-save your Magic URL bookmark — the old link no longer works." });
      reset();
    } catch (err) {
      const d = err.response?.data?.detail;
      setMsg({ type: "err", text: typeof d === "string" ? d : "Could not update the PIN" });
      reset();
    }
    setSaving(false);
  };

  const displayCode = showCurrent ? code : "••••••";

  return (
    <div className="border border-[#C5A059]/30 bg-[#C5A059]/5 p-6 space-y-6" data-testid="access-code-settings">
      <div>
        <p className="font-sans text-[#C5A059] text-[10px] tracking-[0.2em] uppercase mb-2 flex items-center gap-2">
          <KeyRound size={12} /> Current PIN
        </p>
        <div className="flex items-center gap-3">
          <p className="font-mono text-white text-2xl tracking-[0.4em]" data-testid="current-access-code">
            {code ? displayCode : "..."}
          </p>
          <button
            type="button"
            onClick={() => setShowCurrent(s => !s)}
            className="text-white/60 hover:text-[#C5A059] transition-colors"
            aria-label={showCurrent ? "Hide PIN" : "Show PIN"}
            data-testid="toggle-show-pin-btn"
          >
            {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div>
        <p className="font-sans text-white/60 text-[10px] tracking-[0.2em] uppercase mb-2 flex items-center gap-2">
          <Link2 size={12} /> Magic URL — save as bookmark for one-tap login
        </p>
        <div className="flex items-center gap-2 bg-black/40 border border-white/10 p-3">
          <code className="text-[#C5A059] text-[11px] font-mono break-all flex-1" data-testid="magic-url">
            {magicUrl || "..."}
          </code>
          <button
            type="button"
            onClick={copyUrl}
            data-testid="copy-magic-url-btn"
            className="text-white/60 hover:text-[#C5A059] transition-colors shrink-0"
            aria-label="Copy Magic URL"
          >
            {copied ? <Check size={16} className="text-[#C5A059]" /> : <Copy size={16} />}
          </button>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <div className="flex items-center justify-between mb-3">
          <p className="font-sans text-white/60 text-[10px] tracking-[0.2em] uppercase">
            {step === 1 ? "Set a new 6-digit PIN" : "Confirm the new PIN"}
          </p>
          {(newPin || confirmPin) && (
            <button
              type="button"
              onClick={reset}
              className="font-sans text-[10px] tracking-wider uppercase text-white/40 hover:text-[#C5A059] transition-colors flex items-center gap-1"
              data-testid="reset-pin-entry-btn"
            >
              <RotateCcw size={11} /> Restart
            </button>
          )}
        </div>

        {msg && (
          <p
            className={`text-sm font-sans mb-3 ${msg.type === "ok" ? "text-emerald-400" : "text-red-400"}`}
            data-testid="access-code-msg"
          >
            {msg.text}
          </p>
        )}

        <div className="mb-4">
          <PinDots
            value={step === 1 ? newPin : confirmPin}
            maxLength={PIN_LENGTH}
            testid={step === 1 ? "new-pin-dots" : "confirm-pin-dots"}
          />
        </div>

        <div className="max-w-xs mx-auto">
          <PinKeypad
            value={step === 1 ? newPin : confirmPin}
            onChange={step === 1 ? onChangeFirst : onChangeSecond}
            maxLength={PIN_LENGTH}
            disabled={saving}
            testid={step === 1 ? "new-pin-keypad" : "confirm-pin-keypad"}
          />
        </div>

        {saving && (
          <p className="font-sans text-[#C5A059] text-[10px] tracking-[0.25em] uppercase text-center mt-3 animate-pulse" data-testid="pin-saving">
            <Save size={11} className="inline mr-1" /> Saving...
          </p>
        )}

        <p className="font-sans text-white/30 text-[11px] mt-4 leading-relaxed text-center">
          Type your new PIN twice. The keypad has no text input — Safari, Keychain
          and autofill cannot interfere.
        </p>
      </div>
    </div>
  );
};
